/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * A recorded approve or deny, with the evidence it rested on.
 */
export type ApprovalDecision = {
  /**
   * When the decision was made.
   */
  decidedAt: Date;
  /**
   * Who decided.
   */
  decidedBy: string;
  /**
   * Either approved or denied.
   */
  decision: string;
  /**
   * The evidence as it stood when this decision was made. Frozen at decision time: a later re-gather updates the request's evidence but never this snapshot, so what the reviewer actually saw stays inspectable.
   */
  evidence?: any | undefined;
  /**
   * Shape version of the frozen evidence payload, copied from the request at decision time.
   */
  evidenceVersion: number;
  /**
   * Principals the approval covers. Empty for a denial.
   */
  grantedPrincipalUrns: Array<string>;
  /**
   * The decision ID.
   */
  id: string;
  /**
   * Why. Written to be cited when explaining the decision to the requester.
   */
  rationale?: string | undefined;
  /**
   * The research report this decision cited, when one informed it.
   */
  researchReportId?: string | undefined;
};

/** @internal */
export const ApprovalDecision$inboundSchema: z.ZodMiniType<
  ApprovalDecision,
  unknown
> = z.pipe(
  z.object({
    decided_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    decided_by: z.string(),
    decision: z.string(),
    evidence: z.optional(z.any()),
    evidence_version: z.int(),
    granted_principal_urns: z.array(z.string()),
    id: z.string(),
    rationale: z.optional(z.string()),
    research_report_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "decided_at": "decidedAt",
      "decided_by": "decidedBy",
      "evidence_version": "evidenceVersion",
      "granted_principal_urns": "grantedPrincipalUrns",
      "research_report_id": "researchReportId",
    });
  }),
);

export function approvalDecisionFromJSON(
  jsonString: string,
): SafeParseResult<ApprovalDecision, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ApprovalDecision$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ApprovalDecision' from JSON`,
  );
}
